import { apiSlice } from '../../app/api/apiSlice'
import { urlConfig } from '../../config'
import { WithdrawalSource } from '../withdraws/source'

const base = `${urlConfig.walletsBaseUrl}/wallets/crypto-payouts`

// Matches the backend CryptoPayoutAsset enum. USDT_ERC20 is labelled
// "USDT (ERC-20)" in the UI even though the backend testnet proxy resolves it
// against a USDC asset — never surface "USDC" to the user.
export type PayoutAsset =
  | 'BTC'
  | 'LTC'
  | 'DOGE'
  | 'ETH'
  | 'USDT_ERC20'
  | 'USDT_TRC20'

export type PayoutStatus =
  | 'queued'
  | 'awaiting_balance'
  | 'awaiting_approval'
  | 'submitted_to_vaultody'
  | 'broadcast_pending'
  | 'broadcasted'
  | 'confirmed'
  | 'failed'
  | 'manual_review'
  | 'cancelled'
  | 'reversed'

// POST /quote response (crypto-payouts.service.ts quote()).
export interface QuoteResponse {
  quoteId: string
  // Balance the naira is paid from — frozen in the quote.
  source: WithdrawalSource
  asset: PayoutAsset
  amountEnteredNaira: number
  estimatedPayoutBeforeFeesUsd: number
  estimatedFeesUsd: number
  finalAmountUsd: number
  cryptoUnits: string
}

// The persisted CryptoPayout document (confirm / list / detail).
export interface CryptoPayout {
  _id: string
  payoutReference: string
  status: PayoutStatus
  manualReason?: 'insufficient_balance' | 'auto_disabled' | 'over_threshold' | 'no_recent_giftcard_activity' | 'balance_divergence' | 'flagged_account'
  failureReason?: string
  asset: PayoutAsset
  network: string
  destinationAddress: string
  // account (MAIN, default) | reward | referral — absent on older payouts.
  source?: WithdrawalSource
  nairaAmount: number
  settlementRate: number
  coinUsdPrice: number
  baseUsd: number
  fee: {
    networkFeeUsd: number
    riskBufferUsd: number
    feeInNative: string
    maxFeeNative: string
  }
  finalUsd: number
  cryptoUnits: string
  txHash?: string
  transactionRequestId?: string
  confirmations: { current: number; target: number }
  approval: { required: boolean; approvedBy?: string; approvedAt?: string }
  createdAt: string
  updatedAt: string
  submittedAt?: string
  broadcastAt?: string
  confirmedAt?: string
  failedAt?: string
  refundedAt?: string
}

export const cryptoPayoutsSlice = apiSlice.injectEndpoints({
  endpoints: build => ({
    getCryptoPayoutQuote: build.mutation<
      QuoteResponse,
      { asset: PayoutAsset; nairaAmount: number; destinationAddress: string; source?: WithdrawalSource }
    >({
      query: body => ({ url: `${base}/quote`, method: 'POST', body }),
    }),

    // Confirm is money-moving -> gated by 2FA + OTP on the backend. The first
    // call (no OTP headers) triggers a 401 "OTP required" challenge; resubmit
    // with confirmCryptoPayoutWithOtp. Mirrors createWithdraw/-WithOtp.
    confirmCryptoPayout: build.mutation<CryptoPayout, { quoteId: string }>({
      query: body => ({ url: `${base}/confirm`, method: 'POST', body }),
      invalidatesTags: [{ type: 'CryptoPayouts', id: 'PARTIAL-LIST' }],
    }),

    confirmCryptoPayoutWithOtp: build.mutation<
      CryptoPayout,
      { quoteId: string; challengeId: string; otpCode: string }
    >({
      query: ({ quoteId, challengeId, otpCode }) => ({
        url: `${base}/confirm`,
        method: 'POST',
        body: { quoteId },
        headers: { 'x-otp-challenge': challengeId, 'x-otp-code': otpCode },
      }),
      invalidatesTags: [{ type: 'CryptoPayouts', id: 'PARTIAL-LIST' }],
    }),

    listMyCryptoPayouts: build.query<CryptoPayout[], void>({
      query: () => base,
      providesTags: result =>
        result
          ? [
              ...result.map(p => ({ type: 'CryptoPayouts' as const, id: p._id })),
              { type: 'CryptoPayouts', id: 'PARTIAL-LIST' },
            ]
          : [{ type: 'CryptoPayouts', id: 'PARTIAL-LIST' }],
    }),

    getCryptoPayoutById: build.query<CryptoPayout, string>({
      query: id => `${base}/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'CryptoPayoutDetail', id }],
    }),
  }),
})

export const {
  useGetCryptoPayoutQuoteMutation,
  useConfirmCryptoPayoutMutation,
  useConfirmCryptoPayoutWithOtpMutation,
  useListMyCryptoPayoutsQuery,
  useGetCryptoPayoutByIdQuery,
} = cryptoPayoutsSlice
